/**
 * Orden por familia documental de la consulta por numero de DO
 *
 * Es el mismo orden que devuelve gestorapi (App\Services\Genio\OrdenFamiliaDocumental),
 * asi CIMA y Customs AI reciben los documentos igual que los ve la persona.
 * Los padres siguen la regla de la lista de chequeo de CIMA
 * (ChecklistHierarchyService::sortHierarchy):
 *   1. Documento de transporte (AREA 100 / SERIE 100) seguido de sus hijos:
 *      consulta de inventarios, manifiesto, cuenta de fletes.
 *   2. Cada factura comercial (100 / 102 / 143), por numero, seguida de sus hijos:
 *      lista de empaque, certificado de origen, certificacion del proveedor.
 *      El numero es el DETDOCXX de CIMA y se compara igual (sin numero primero).
 *   3. Padres de GEST0016 que no estan en la lista de chequeo, por nombre del tipo.
 *   4. Documentos que no cuelgan de un padre, por nombre del tipo documental.
 */
import { numeroDocumentoDe } from "./tradicion";

export const FAMILIA_TRANSPORTE = 0;
export const FAMILIA_FACTURA = 1;
export const FAMILIA_OTRO_PADRE = 2;

/** Metadatos del numero por familia: los mismos del DETDOCXX de la lista de chequeo de CIMA */
const LLAVES_NUMERO = {
  [FAMILIA_TRANSPORTE]: ["NUMBLXXX", "DOCTRANS", "DOCNUMXX"],
  [FAMILIA_FACTURA]: ["FACOMERX", "DOCNUMXX", "DETALLEX"],
};

/**
 * Orden de los hijos dentro de su padre, por palabra clave del tipo documental.
 * Los hijos que no coinciden van despues, por nombre del tipo.
 */
const ORDEN_HIJOS = [
  "INVENTARIO", // consulta de inventarios (transporte)
  "MANIFIESTO", // manifiesto de carga (transporte)
  "FLETE", // cuenta / certificacion de fletes (transporte)
  "EMPAQUE", // lista de empaque (factura)
  "ORIGEN", // certificado de origen (factura)
  "PROVEEDOR", // certificacion del proveedor (factura)
];

const normalizar = (valor) =>
  String(valor ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toUpperCase();

/**
 * Comparacion natural: "F-9" antes que "F-10"
 * @param {*} a
 * @param {*} b
 * @returns {number}
 */
const compararTexto = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""), "es", { numeric: true, sensitivity: "base" });

/** Vacios al final; entre dos con valor, comparacion natural */
const compararConVacioAlFinal = (a, b) => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return compararTexto(a, b);
};

const compararId = (a, b) => compararTexto(a.IDARCHXX, b.IDARCHXX);

/**
 * Familia documental de un documento padre, con los criterios de la lista de
 * chequeo de CIMA (isInvoice / isTransport)
 * @param {object} row
 * @returns {number} FAMILIA_TRANSPORTE | FAMILIA_FACTURA | FAMILIA_OTRO_PADRE
 */
export const familiaDe = (row) => {
  const area = Number(row?.AREAIDXX);
  const serie = Number(row?.SERIEIDX);
  if (area === 100 && serie === 102 && Number(row?.TIPOIDXX) === 143) return FAMILIA_FACTURA;
  if (area === 100 && serie === 100) return FAMILIA_TRANSPORTE;
  return FAMILIA_OTRO_PADRE;
};

/**
 * Numero del padre como el DETDOCXX de la lista de chequeo de CIMA
 * @param {object} row
 * @returns {string} cadena vacia si no tiene
 */
const numeroCima = (row) => {
  const meta = row?.JSONMETX || {};
  for (const llave of LLAVES_NUMERO[familiaDe(row)] || []) {
    const valor = (Array.isArray(meta[llave]) ? meta[llave] : [meta[llave]])
      .map((v) => String(v ?? "").trim())
      .filter(Boolean)
      .join(", ");
    if (valor) return valor;
  }
  return "";
};

/** Comparacion binaria, igual al strcmp de PHP */
const strcmp = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

/**
 * Transporte, luego facturas (como la lista de chequeo de CIMA: por numero, los
 * que no tienen numero primero) y al final los padres de GEST0016 que no estan
 * en la lista de chequeo, por nombre del tipo documental.
 */
export const compararPadres = (a, b) => {
  const familia = familiaDe(a) - familiaDe(b);
  if (familia !== 0) return familia;
  if (familiaDe(a) === FAMILIA_OTRO_PADRE) {
    return (
      compararTexto(a.TIPODESX, b.TIPODESX) ||
      compararConVacioAlFinal(numeroDocumentoDe(a), numeroDocumentoDe(b)) ||
      compararId(a, b)
    );
  }
  return strcmp(numeroCima(a), numeroCima(b)) || compararId(a, b);
};

const rangoHijo = (row) => {
  const tipo = normalizar(row?.TIPODESX);
  const indice = ORDEN_HIJOS.findIndex((clave) => tipo.includes(clave));
  return indice === -1 ? ORDEN_HIJOS.length : indice;
};

/** Hijos de un padre: orden de ORDEN_HIJOS, luego tipo documental y numero */
export const compararHijos = (a, b) =>
  rangoHijo(a) - rangoHijo(b) ||
  compararTexto(a.TIPODESX, b.TIPODESX) ||
  compararConVacioAlFinal(numeroDocumentoDe(a), numeroDocumentoDe(b)) ||
  compararId(a, b);

/** Documentos sin padre: por nombre del tipo documental */
export const compararOtros = (a, b) => compararTexto(a.TIPODESX, b.TIPODESX) || compararId(a, b);
